/**
 * FEN reading for any 8×8 variant position, without a rules engine: piece placement
 * (including Crazyhouse's "~" promoted marker), pockets, and Three-check counters.
 * chess.js rejects many legal variant positions (a Horde pawn on the first rank,
 * Atomic kings side by side), so the boards read pieces from here instead.
 */
import type { Color, PieceSymbol } from "chess.js";

export type BoardPiece = { type: PieceSymbol; color: Color; promoted?: boolean };
export type Pockets = Record<Color, Partial<Record<PieceSymbol, number>>>;

const FILES = "abcdefgh";
const PIECES = new Set(["p", "n", "b", "r", "q", "k"]);

/** The placement field with any pocket removed: "…/RNBQKBNR[Pp]" and "…/RNBQKBNR/Pp" both work. */
function placementAndPocket(fen: string): { ranks: string[]; pocket: string | null } {
  const field = fen.trim().split(/\s+/)[0] ?? "";
  const bracket = field.match(/^(.*)\[(.*)\]$/);
  if (bracket) return { ranks: bracket[1].split("/"), pocket: bracket[2] };
  const ranks = field.split("/");
  if (ranks.length === 9) return { ranks: ranks.slice(0, 8), pocket: ranks[8] };
  return { ranks, pocket: null };
}

/** Square → piece for an 8×8 FEN. Unknown letters are skipped rather than thrown on. */
export function parsePlacement(fen: string): Map<string, BoardPiece> {
  const { ranks } = placementAndPocket(fen);
  const out = new Map<string, BoardPiece>();
  ranks.slice(0, 8).forEach((row, i) => {
    const rank = 8 - i;
    let file = 0;
    for (let j = 0; j < row.length; j++) {
      const ch = row[j];
      if (/\d/.test(ch)) {
        let n = ch;
        while (j + 1 < row.length && /\d/.test(row[j + 1])) n += row[++j];
        file += Number(n);
        continue;
      }
      if (ch === "~") continue;
      const lower = ch.toLowerCase();
      if (!PIECES.has(lower)) continue;
      if (file < 8) {
        const promoted = row[j + 1] === "~";
        out.set(`${FILES[file]}${rank}`, { type: lower as PieceSymbol, color: ch === lower ? "b" : "w", ...(promoted ? { promoted } : {}) });
      }
      file++;
    }
  });
  return out;
}

export function parsePockets(fen: string): Pockets | null {
  const { pocket } = placementAndPocket(fen);
  if (pocket === null) return null;
  const out: Pockets = { w: {}, b: {} };
  for (const ch of pocket) {
    const lower = ch.toLowerCase();
    if (!PIECES.has(lower)) continue;
    const side = ch === lower ? out.b : out.w;
    side[lower as PieceSymbol] = (side[lower as PieceSymbol] ?? 0) + 1;
  }
  return out;
}

/** Three-check: the "2+3" field holds the checks each side still needs (White first). */
export function checksRemaining(fen: string): Record<Color, number> | null {
  const field = fen.trim().split(/\s+/).find((f) => /^\d+\+\d+$/.test(f));
  if (!field) return null;
  const [w, b] = field.split("+").map(Number);
  return { w, b };
}

export function sideToMove(fen: string): Color {
  return fen.trim().split(/\s+/)[1] === "b" ? "b" : "w";
}

export function findKing(fen: string, color: Color): string | null {
  for (const [sq, p] of parsePlacement(fen)) if (p.type === "k" && p.color === color) return sq;
  return null;
}

/**
 * Chess960 start position by Scharnagl number (0–959); 518 is the standard setup.
 * Castling rights use rook files (Shredder-FEN), which ffish and Stockfish both read.
 */
export function chess960Fen(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 959) throw new RangeError(`Chess960 positions are numbered 0–959, not ${n}`);
  const rank: (string | null)[] = Array(8).fill(null);
  let x = n;
  rank[(x % 4) * 2 + 1] = "b"; x = Math.floor(x / 4);
  rank[(x % 4) * 2] = "b"; x = Math.floor(x / 4);
  const empty = () => rank.flatMap((p, i) => (p === null ? [i] : []));
  rank[empty()[x % 6]] = "q"; x = Math.floor(x / 6);
  const KNIGHTS = [[0, 1], [0, 2], [0, 3], [0, 4], [1, 2], [1, 3], [1, 4], [2, 3], [2, 4], [3, 4]][x];
  const spaces = empty();
  for (const k of KNIGHTS) rank[spaces[k]] = "n";
  const [r1, k, r2] = empty();
  rank[r1] = "r"; rank[k] = "k"; rank[r2] = "r";
  const black = rank.join("");
  const rights = `${FILES[r2].toUpperCase()}${FILES[r1].toUpperCase()}${FILES[r2]}${FILES[r1]}`;
  return `${black}/pppppppp/8/8/8/8/PPPPPPPP/${black.toUpperCase()} w ${rights} - 0 1`;
}

export const STANDARD_960 = 518;
